/**
 * A wizard draft: the state that has to survive a reload, a browser Back, or an
 * accidental tab close, so a half-filled study is not silently thrown away.
 *
 * The marketplace wizard already had a good implementation of this. The enterprise
 * wizards had none at all, so the obvious move was to copy it — which is exactly how the
 * image-retention bug ended up living in five separate form files. The settled parts
 * (per-user scoping, purge-on-read, soft-fail when storage is unusable) live here once;
 * callers differ only in namespace, backend and lifetime.
 *
 * Why the backend is a parameter rather than a constant: the marketplace draft carries a
 * reusable `studyId`, so it must die with the tab — resurrecting it days later could
 * re-attach to a study the user has already paid for. The enterprise draft creates
 * nothing server-side until Run, so that hazard does not exist, and the case users keep
 * reporting is an accidental tab close. See specs/27/decisions-02.md.
 */

export type DraftStorageKind = 'session' | 'local';

export interface DraftStoreOptions {
  /** Storage key prefix. Include a version suffix so a shape change cannot half-restore. */
  namespace: string;
  storage: DraftStorageKind;
  /** Older than this on read and the draft is purged rather than returned. */
  maxAgeMs: number;
}

export type Stamped<T> = T & { updatedAt: number };

export interface DraftStore<T> {
  save(patch: Partial<T>): void;
  load(): Stamped<T> | null;
  clear(): void;
  /** The user the draft belongs to; see scopedKey below. */
  setScope(id: string | number | null): void;
}

export function createDraftStore<T extends object>(options: DraftStoreOptions): DraftStore<T> {
  const { namespace, storage, maxAgeMs } = options;

  /**
   * Which user this draft belongs to.
   *
   * Without it, user B on a shared machine (same browser, after A logged out) reads A's
   * draft — A's typed questions, and in the marketplace A's reusable studyId. Scoping the
   * key makes B's key a different key, so one account cannot reach another's. This matters
   * MORE for a localStorage-backed store than the original sessionStorage one, because
   * the draft now outlives the tab.
   */
  let scopeId: string | number | null = null;

  // Read lazily and inside try/catch: touching localStorage throws outright in some
  // sandboxed/blocked contexts, and that must degrade to "no draft", never to a crash.
  const backing = (): Storage => (storage === 'local' ? localStorage : sessionStorage);

  const scopedKey = (): string => (scopeId != null ? `${namespace}.${scopeId}` : namespace);

  const load = (): Stamped<T> | null => {
    try {
      const raw = backing().getItem(scopedKey());
      if (!raw) return null;
      const draft = JSON.parse(raw) as Stamped<T>;
      // A non-object (hand-edited, or a half-written entry) must not be spread into the
      // wizard's state, where every field silently reads undefined and resets.
      if (!draft || typeof draft !== 'object' || Array.isArray(draft)) return null;
      if (Date.now() - (draft.updatedAt || 0) > maxAgeMs) {
        clear();
        return null;
      }
      return draft;
    } catch {
      return null;
    }
  };

  const save = (patch: Partial<T>): void => {
    try {
      const next = { ...(load() || {}), ...patch, updatedAt: Date.now() };
      backing().setItem(scopedKey(), JSON.stringify(next));
    } catch {
      // Private mode, quota, or blocked storage. Losing persistence is a degraded wizard;
      // throwing here would be a broken one.
    }
  };

  const clear = (): void => {
    try {
      backing().removeItem(scopedKey());
    } catch {
      /* nothing to do */
    }
  };

  return {
    save,
    load,
    clear,
    setScope: (id) => { scopeId = id; },
  };
}
